import React, { useState, useEffect, useRef } from 'react';
import { HiQrcode, HiCheck, HiX, HiUser, HiCalendar, HiCamera, HiStop, HiOutlineMail, HiOutlineOfficeBuilding, HiOutlineUserCircle, HiOutlineTicket, HiOutlineChartBar, HiOutlineCheckCircle, HiOutlineCalendar } from 'react-icons/hi';
import './PointageAppareil.css';
import { visiteursService } from '../services/visiteursService';
import { supabase } from '../supabaseClient';
import jsQR from 'jsqr';
import QRScannerCustom from './QRScannerCustom';
import emailjs from '@emailjs/browser'; // Add this import

function PointageAppareil() {
  const [scanResult, setScanResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [visiteurInfo, setVisiteurInfo] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [stream, setStream] = useState(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [scanMode, setScanMode] = useState('manual'); // 'manual' or 'camera'
  const [loading, setLoading] = useState(false);
  const [showCustomScanner, setShowCustomScanner] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const inputRef = useRef(null); // Add this ref for the input field

  // Check if device has camera
  useEffect(() => {
    const checkCamera = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideoInput = devices.some(device => device.kind === 'videoinput');
        setHasCamera(hasVideoInput);
      } catch (error) {
        console.error('Error checking camera:', error);
        setHasCamera(false);
      }
    };

    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      checkCamera();
    }
  }, []);

  // Fix: Use window.BarcodeDetector for support check
  const isBarcodeDetectorSupported = 'BarcodeDetector' in window && window.BarcodeDetector.getSupportedFormats;

  // BarcodeDetector-based scan
  const startBarcodeDetectorScan = async () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    let detector;
    try {
      detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    } catch (e) {
      alert("BarcodeDetector API non supportée.");
      return;
    }
    setIsScanning(true);
    setScanMode("camera");

    const scan = async () => {
      if (!videoRef.current || !isScanning) return;
      try {
        // Draw current video frame to canvas
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const bitmap = await createImageBitmap(canvas);

        const barcodes = await detector.detect(bitmap);
        if (barcodes.length > 0) {
          const qrData = barcodes[0].rawValue;
          setManualInput(qrData);
          setScanMode('manual');
          setIsScanning(false);
          stopCamera();
          setTimeout(() => {
            if (inputRef.current) inputRef.current.focus();
          }, 100);
          return;
        }
      } catch (err) {
        // Ignore and continue scanning
      }
      if (isScanning) {
        scanIntervalRef.current = setTimeout(scan, 500);
      }
    };
    scan();
  };

  const startCamera = async () => {
    try {
      // Always use environment camera for mobile
      const constraints = {
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => {
            if (isBarcodeDetectorSupported) {
              startBarcodeDetectorScan();
            } else {
              setIsScanning(true);
              setScanMode("camera");
              startQRScan();
            }
          }).catch(err => {
            console.error("Video play failed:", err);
            alert("Impossible de démarrer la vidéo de la caméra.");
          });
        };
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert(
        "Impossible d'accéder à la caméra. Vérifiez vos permissions et que l'application est servie en HTTPS."
      );
    }
};


  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    
    setIsScanning(false);
    setScanMode('manual');
  };

  const startQRScan = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    
    scanIntervalRef.current = setInterval(() => {
      captureAndAnalyze();
    }, 500); // Scan every 500ms
  };

  const captureAndAnalyze = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (video.videoWidth === 0 || video.videoHeight === 0) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

    try {
      const qrData = await simulateQRDetection(imageData);
      if (qrData) {
        // Set QR value in manual input and switch to manual mode for confirmation
        setManualInput(qrData);
        setScanMode('manual');
        setIsScanning(false);
        stopCamera();
        setTimeout(() => {
          if (inputRef.current) inputRef.current.focus();
        }, 100);
      }
    } catch (error) {
      // Continue scanning
    }
  };

  // Use jsQR for real QR detection
  const simulateQRDetection = async (imageData) => {
    // Use jsQR to detect QR code in the image data
    const code = jsQR(imageData.data, imageData.width, imageData.height);
    return code ? code.data : null;
  };

  const handleManualInput = async () => {
    if (manualInput.trim()) {
      await processQRData(manualInput.trim());
    }
  };

  const processQRData = async (qrData) => {
    try {
      setLoading(true);
      let reference;
      
      // Try to parse as JSON first (from QR code)
      try {
        const parsed = JSON.parse(qrData);
        reference = parsed.reference;
      } catch {
        // If not JSON, treat as direct reference
        reference = qrData;
      }

      const visiteur = await visiteursService.getVisiteurByReference(reference);
      
      if (visiteur) {
        setVisiteurInfo({
          id: visiteur.id,
          nom: visiteur.nom,
          prenom: visiteur.prenom,
          email: visiteur.email,
          telephone: visiteur.telephone,
          organisation: visiteur.organisation,
          profil: visiteur.profil,
          dateInscription: visiteur.date_inscription,
          statut: visiteur.statut,
          referenceParticipation: visiteur.reference_participation,
          conferenceTitre: visiteur.conferences?.titre || 'Conférence supprimée'
        });
        setScanResult('success');
      } else {
        setScanResult('error');
        setVisiteurInfo(null);
      }
    } catch (error) {
      console.error('Error processing QR data:', error);
      setScanResult('error');
      setVisiteurInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleValidatePresence = async () => {
    if (visiteurInfo) {
      try {
        await visiteursService.markPresence(visiteurInfo.id);
        // Send welcome email after marking as present
        await visiteursService.sendWelcomeEmail(visiteurInfo.id);

        // Add to scan history
        const scanEntry = {
          id: Date.now(),
          visiteur: visiteurInfo,
          timestamp: new Date().toISOString(),
          action: 'marked_present'
        };
        
        setScanHistory(prev => [scanEntry, ...prev.slice(0, 9)]); // Keep last 10
        
        // Reset
        setVisiteurInfo(null);
        setScanResult(null);
        setManualInput('');
      } catch (error) {
        console.error('Error marking presence:', error);
        alert('Erreur lors du marquage de la présence. Veuillez réessayer.');
      }
    }
  };

  const resetScan = () => {
    setScanResult(null);
    setVisiteurInfo(null);
    setManualInput('');
    if (isScanning) {
      stopCamera();
    }
  };

  // Handler for QRScannerCustom result
  const handleCustomQRResult = (qrData) => {
    setManualInput(qrData);
    setScanMode('manual');
    setShowCustomScanner(false);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, [stream]);

  const profilLabels = {
    'student': 'Étudiant',
    'professor': 'Professeur',
    'professional': 'Professionnel'
  };

  const statutLabels = {
    'en_attente': 'En Attente',
    'valide': 'Validé',
    'rejete': 'Rejeté',
    'present': 'Présent'
  };

  // Add this function to match VisiteursManagement logic
  const handleSendWelcomeEmail = async (visiteurId) => {
    try {
      // Fetch visiteur info (already in visiteurInfo)
      const v = visiteurInfo;
      if (!v || v.id !== visiteurId) return;

      const to_email = v.email || '';
      const to_name = `${v.prenom || ''} ${v.nom || ''}`.trim();
      const conference_titre = v.conferenceTitre || '';
      // If you have more conference info, add it here
      const conference_date = ''; // Not available in visiteurInfo, unless you add it
      const conference_salle = ''; // Not available in visiteurInfo, unless you add it

      const emailVars = {
        to_email,
        to_name,
        conference_titre,
        conference_date,
        conference_salle,
      };

      await emailjs.send(
        'service_dyae2ak',
        'template_2tcwp6f', // Same template as VisiteursManagement
        emailVars,
        '_UeBfJMtIUI_gP-mW'
      );
      alert('Email de bienvenue envoyé !');
    } catch (error) {
      alert('Erreur lors de l\'envoi de l\'email de bienvenue.');
    }
  };

  return (
    <div className="pointage-page">
      <div className="pointage-container">
        <div className="pointage-header">
          <h1><HiQrcode /> Pointage des Participants</h1>
          <p>Scannez le code QR avec la caméra ou saisissez la référence manuellement</p>
        </div>

        <div className="scan-section">
          {/* Scan Mode Toggle */}
          <div className="scan-mode-toggle">
            <button 
              className={`mode-btn ${scanMode === 'manual' ? 'active' : ''}`}
              onClick={() => {
                if (isScanning) stopCamera();
                setScanMode('manual');
                setShowCustomScanner(false);
              }}
            >
              Saisie manuelle
            </button>

            {hasCamera && (
              <button 
                className={`mode-btn ${scanMode === 'camera' ? 'active' : ''}`}
                onClick={() => {
                  if (scanMode === 'camera') {
                    setShowCustomScanner(false);
                    stopCamera();
                    setScanMode('manual');
                  } else {
                    setShowCustomScanner(true);
                    setScanMode('camera');
                  }
                }}
              >
                {scanMode === 'camera' ? (
                  <>
                    <HiStop /> Arrêter
                  </>
                ) : (
                  <>
                    <HiCamera /> Scanner QR
                  </>
                )}
              </button>
            )}
          </div>

          {/* Camera View with QRScannerCustom */}
          {scanMode === 'camera' && showCustomScanner && (
            <div className="camera-section">
              <QRScannerCustom onResult={handleCustomQRResult} />
            </div>
          )}

          {/* Camera View */}
          {scanMode === 'camera' && (
            <div className="camera-section">
              <div className="camera-container" style={{ position: "relative" }}>
                <video 
                  ref={videoRef}
                  className="camera-video"
                  autoPlay 
                  playsInline 
                  muted
                  width={400}
                  height={300}
                  style={{
                    background: "#000",
                    border: "2px solid #4caf50",
                    display: "block",
                    maxWidth: "100%",
                    zIndex: 1
                  }}
                />
                <canvas 
                  ref={canvasRef}
                  style={{ display: 'none' }}
                />
                {/* Optional: keep overlay but make sure it's transparent and doesn't block video */}
                <div className="scan-overlay" style={{ pointerEvents: "none", zIndex: 2 }}>
                  <div className="scan-frame" style={{ border: "2px dashed #4caf50", background: "transparent" }}>
                    <div className="scan-corners">
                      <div className="corner top-left"></div>
                      <div className="corner top-right"></div>
                      <div className="corner bottom-left"></div>
                      <div className="corner bottom-right"></div>
                    </div>
                  </div>
                </div>
                {isScanning && (
                  <div className="scan-status" style={{ zIndex: 3 }}>
                    <div className="scanning-indicator">
                      <div className="pulse"></div>
                      <span>Recherche de code QR...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manual Input */}
          {scanMode === 'manual' && (
            <div className="manual-input">
              <h3>Saisie manuelle de la référence</h3>
              <div className="input-group">
                <input
                  ref={inputRef}
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="GSIDA1234567890"
                  className="reference-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualInput()}
                  disabled={loading}
                />
                <button 
                  onClick={handleManualInput} 
                  className="scan-btn"
                  disabled={loading || !manualInput.trim()}
                >
                  {loading ? 'Vérification...' : 'Vérifier'}
                </button>
              </div>
            </div>
          )}

          {scanResult && (
            <div className={`scan-result ${scanResult}`}>
              {scanResult === 'success' && visiteurInfo ? (
                <div className="visiteur-found">
                  <div className="success-icon">
                    <HiCheck />
                  </div>
                  <h3>Participant trouvé !</h3>
                  
                  <div className="visiteur-details">
                    <div className="detail-row">
                      <HiUser />
                      <span>{visiteurInfo.prenom} {visiteurInfo.nom}</span>
                    </div>
                    <div className="detail-row">
                      <HiOutlineMail />
                      <span>{visiteurInfo.email}</span>
                    </div>
                    <div className="detail-row">
                      <HiOutlineOfficeBuilding />
                      <span>{visiteurInfo.organisation || 'Non renseigné'}</span>
                    </div>
                    <div className="detail-row">
                      <HiOutlineUserCircle />
                      <span>{profilLabels[visiteurInfo.profil] || visiteurInfo.profil}</span>
                    </div>
                    <div className="detail-row">
                      <HiCalendar />
                      <span>Inscrit le {visiteurInfo.dateInscription}</span>
                    </div>
                    <div className="detail-row">
                      <HiOutlineTicket />
                      <span>{visiteurInfo.referenceParticipation}</span>
                    </div>
                    <div className="detail-row">
                      <HiOutlineChartBar />
                      <span className={`status-badge status-${visiteurInfo.statut}`}>
                        {statutLabels[visiteurInfo.statut] || visiteurInfo.statut}
                      </span>
                    </div>
                    <div className="detail-row">
                      <HiOutlineCalendar />
                      <span>{visiteurInfo.conferenceTitre}</span>
                    </div>
                  </div>

                  {visiteurInfo.statut !== 'present' ? (
                    <div className="action-buttons">
                      <button
                        onClick={async () => {
                          await handleValidatePresence();
                          await handleSendWelcomeEmail(visiteurInfo.id);
                        }}
                        className="validate-btn"
                      >
                        <HiCheck /> Marquer comme Présent & Envoyer email de bienvenue
                      </button>
                      <button onClick={resetScan} className="cancel-btn">
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <div className="already-present">
                      <p><HiOutlineCheckCircle /> Ce participant est déjà marqué comme présent</p>
                      <button onClick={resetScan} className="new-scan-btn">
                        Nouveau scan
                      </button>
                      <button
                        onClick={async () => {
                          await handleSendWelcomeEmail(visiteurInfo.id);
                        }}
                        className="send-welcome-btn"
                        style={{ marginLeft: '10px' }}
                      >
                        Envoyer email de bienvenue
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="visiteur-not-found">
                  <div className="error-icon">
                    <HiX />
                  </div>
                  <h3>Participant non trouvé</h3>
                  <p>Référence invalide ou participant non inscrit</p>
                  <button onClick={resetScan} className="retry-btn">
                    Réessayer
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Always show welcome email button if participant is present */}
          {visiteurInfo && visiteurInfo.statut === 'present' && (
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                onClick={async () => {
                  try {
                    await visiteursService.sendWelcomeEmail(visiteurInfo.id);
                    alert('Email de bienvenue envoyé !');
                  } catch (error) {
                    alert('Erreur lors de l\'envoi de l\'email de bienvenue.');
                  }
                }}
                className="send-welcome-btn"
                style={{ marginLeft: '10px' }}
              >
                Envoyer email de bienvenue
              </button>
            </div>
          )}
        </div>

        {scanHistory.length > 0 && (
          <div className="scan-history">
            <h3>Historique des scans</h3>
            <div className="history-list">
              {scanHistory.map(entry => (
                <div key={entry.id} className="history-item">
                  <div className="history-info">
                    <span className="history-name">
                      {entry.visiteur.prenom} {entry.visiteur.nom}
                    </span>
                    <span className="history-time">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <span className="history-status"><HiOutlineCheckCircle /> Présent</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PointageAppareil;

